/**
 * @enum {string}
 * @readonly
 */
export const AuthType = {
  NONE: 'none',
  API_TOKEN: 'api-token',
  API_TOKEN_BASIC: 'api-token-basic',
  SESSION_TOKEN: 'session-token',
  SESSION_TOKEN_AND_ACCESS_TOKEN_URL: 'session-and-access-token-url',
  SESSION_TOKEN_AND_ACCESS_TOKEN: 'session-and-access-token',
  CONSUMER: 'consumer',
  SSO_TOKEN: 'sso-token',
};

/**
 * @enum {string}
 * @readonly
 */
const TokenType = {
  BASIC: 'basic',
  BEARER: 'bearer',
};

/**
 * @typedef AuthenticationConfig
 * @type {object}
 * @property {AuthType} [authType]
 */

/**
 * @callback GetSessionTokenCallback
 * @param {import('axios').AxiosRequestConfig} restRequestConfig
 * @returns {string | Promise<string>}
 */

/**
 * @callback GetAccessTokenCallback
 * @param {import('axios').AxiosRequestConfig} restRequestConfig
 * @returns {string | Promise<string>}
 */

/**
 * @typedef Consumer
 * @type {object}
 * @property {string} key
 * @property {string} secret
 */

/**
 * @callback GetConsumerCallback
 * @param {import('axios').AxiosRequestConfig} restRequestConfig
 * @returns {Consumer | Promise<Consumer>}
 */

/**
 * @typedef AuthenticationInterceptorCreatorParams
 * @type {object}
 * @property {string} [apiToken]
 * @property {GetSessionTokenCallback} [getSessionToken]
 * @property {GetAccessTokenCallback} [getAccessToken]
 * @property {GetConsumerCallback} [getConsumer]
 */

/**
 * @param {AuthenticationInterceptorCreatorParams} param0
 */
export function createAuthenticationInterceptor({
  apiToken,
  getSessionToken,
  getAccessToken,
  getConsumer,
  getSsoToken,
}) {
  /**
   * @param {AuthenticationConfig & import('axios').AxiosRequestConfig} param0
   */
  async function interceptor({ authType, ...axiosRequestConfig }) {
    const { url } = axiosRequestConfig;
    let newUrl = url;
    let token;
    let accessToken;
    let tokenRequired = false;
    let tokenType;

    switch (authType) {
      case AuthType.API_TOKEN_BASIC:
        tokenRequired = true;
        token = apiToken;
        tokenType = TokenType.BASIC;
        break;
      case AuthType.API_TOKEN:
        tokenRequired = true;
        token = apiToken;
        tokenType = TokenType.BEARER;
        break;
      case AuthType.SESSION_TOKEN:
        tokenRequired = true;
        token = getSessionToken(axiosRequestConfig);
        tokenType = TokenType.BEARER;
        break;
      case AuthType.SESSION_TOKEN_AND_ACCESS_TOKEN_URL:
        tokenRequired = true;
        accessToken = getAccessToken(axiosRequestConfig);
        token = getSessionToken(axiosRequestConfig);
        tokenType = TokenType.BEARER;
        newUrl = url.replace('{accessToken}', await accessToken);
        break;
      case AuthType.SESSION_TOKEN_AND_ACCESS_TOKEN:
        tokenRequired = true;
        accessToken = await getAccessToken(axiosRequestConfig);
        token = await getSessionToken(axiosRequestConfig);
        return {
          ...axiosRequestConfig,
          headers: { ...axiosRequestConfig.headers, Authorization: `Bearer ${token}` },
          data: { ...axiosRequestConfig.data, token: accessToken },
        };
      case AuthType.CONSUMER: {
        const { key, secret } = await getConsumer(axiosRequestConfig);
        return {
          ...axiosRequestConfig,
          headers: {
            ...axiosRequestConfig.headers,
            'x-consumer-key': key,
            'x-consumer-secret': secret,
          },
        };
      }
      case AuthType.SSO_TOKEN: {
        const ssoToken = await getSsoToken();
        return {
          ...axiosRequestConfig,
          headers: { ...axiosRequestConfig.headers, ssoToken },
        };
      }
      case AuthType.NONE:
      default:
        return axiosRequestConfig;
    }

    token = await token;

    if (tokenRequired && !token) {
      if (tokenType === TokenType.BEARER) {
        throw new Error('A bearer token is required but none was provided');
      } else if (tokenType === TokenType.BASIC) {
        throw new Error('A basic token is required but none was provided');
      }
    }

    if (token) {
      let authorizationHeader;

      switch (tokenType) {
        case TokenType.BASIC:
          authorizationHeader = `Basic ${token}`;
          break;
        case TokenType.BEARER:
        default:
          authorizationHeader = `Bearer ${token}`;
          break;
      }

      return {
        ...axiosRequestConfig,
        url: newUrl,
        headers: {
          ...axiosRequestConfig.headers,
          Authorization: authorizationHeader,
        },
      };
    }

    return axiosRequestConfig;
  }

  return interceptor;
}
